import { Extension } from "@tiptap/core";
import Suggestion from "@tiptap/suggestion";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import ReferenceSuggestionList from "@/components/ReferenceSuggestionList";
import {
  channelReferenceHref,
  teamReferenceHref,
  userReferenceHref,
} from "@/lib/contentReferences";
import { searchContentReferences } from "@/lib/referencesApi";
import { listSocialDmMentionables } from "@/lib/socialDmApi";
import { socialUserReferenceHref } from "@/lib/contentReferences";

const userSuggestionPluginKey = new PluginKey("circaUserSuggestion");
const channelSuggestionPluginKey = new PluginKey("circaChannelSuggestion");

function insertReferenceLink(editor, range, { href, label }) {
  const chain = editor.chain().focus();
  if (range && range.from !== range.to) {
    chain.deleteRange(range);
  }
  chain
    .insertContent([
      {
        type: "text",
        text: label,
        marks: [
          {
            type: "link",
            attrs: {
              href,
              target: null,
              rel: null,
              class: "circa-reference-link",
            },
          },
        ],
      },
      { type: "text", text: " " },
    ])
    // Ensure the caret exits the link mark before the user keeps typing.
    .unsetMark("link")
    .run();
}

function getFullName(user) {
  return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "User";
}

function buildSuggestion({
  char,
  kind,
  getContext,
  pluginKey,
}) {
  return {
    pluginKey,
    char,
    allowSpaces: false,
    startOfLine: false,
    items: async ({ query }) => {
      const ctx = getContext?.() || {};
      if (!ctx.accessToken || !ctx.enabled) return [];
      if (ctx.socialMode && ctx.conversationId) {
        if (kind !== "user") return [];
        try {
          const users = await listSocialDmMentionables(
            ctx.accessToken,
            ctx.conversationId,
            query
          );
          return (users || []).map((u) => ({
            id: `social-user-${u.user_id}`,
            kind: "social-user",
            label: `@${u.display_name || u.username || "User"}`,
            subtitle: u.username ? `@${u.username}` : "",
            targetId: u.user_id,
            displayName: u.display_name || u.username || "User",
            username: u.username,
            sortKey: String(u.display_name || u.username || "").toLowerCase(),
          }));
        } catch (err) {
          console.error("Social mention search failed:", err);
          return [];
        }
      }
      if (!ctx.orgId) return [];
      try {
        const types =
          kind === "channel"
            ? ["channel"]
            : ctx.contextType === "post"
              ? ["user", "team"]
              : ["user"];
        const data = await searchContentReferences(ctx.accessToken, {
          orgId: ctx.orgId,
          q: query,
          types,
          conversationId: ctx.conversationId,
          contextType: ctx.contextType,
        });
        if (kind === "channel") {
          return (data.channels || []).map((c) => ({
            id: `channel-${c.conversation_id}`,
            kind: "channel",
            label: `#${c.title || "channel"}`,
            subtitle: c.team_id ? `Team #${c.team_id}` : "Channel",
            targetId: String(c.conversation_id),
            title: c.title || "channel",
          }));
        }

        const users = (data.users || []).map((u) => {
          const name = getFullName(u);
          const canInvite = Boolean(u.can_invite);
          const isBot = Boolean(u.is_bot);
          return {
            id: `user-${u.user_id}`,
            kind: canInvite ? "invite" : "user",
            label: canInvite ? `Invite and mention ${name}` : `@${name}`,
            subtitle: isBot ? "Bot" : u.title || u.email || "",
            targetId: u.user_id,
            displayName: name,
            user: u,
            canInvite,
            isBot,
            sortKey: name.toLowerCase(),
          };
        });

        const teams = (data.teams || []).map((t) => ({
          id: `team-${t.team_id}`,
          kind: "team",
          label: `@${t.name || "Team"}`,
          subtitle:
            t.visibility === "restricted" ? "Restricted team" : "Team",
          targetId: String(t.team_id),
          displayName: t.name || "Team",
          team: t,
          sortKey: String(t.name || "").toLowerCase(),
        }));

        const q = String(query || "").trim().toLowerCase();
        const hereAllowed =
          ctx.contextType === "channel" || ctx.contextType === "group";
        const hereMatches = hereAllowed && (q === "" || "here".startsWith(q));
        const hereItem = hereMatches
          ? [
              {
                id: "special-here",
                kind: "here",
                label: "@here",
                subtitle:
                  ctx.contextType === "group"
                    ? "Notify everyone in this group"
                    : "Notify everyone in this channel",
                sortKey: "here",
              },
            ]
          : [];

        return [...hereItem, ...users, ...teams]
          .sort((a, b) => {
            // Keep @here pinned above people/teams when present.
            if (a.kind === "here") return -1;
            if (b.kind === "here") return 1;
            return a.sortKey.localeCompare(b.sortKey);
          })
          .slice(0, 20);
      } catch (err) {
        console.error("Reference search failed:", err);
        const q = String(query || "").trim().toLowerCase();
        if (
          kind === "user" &&
          (ctx.contextType === "channel" || ctx.contextType === "group") &&
          (q === "" || "here".startsWith(q))
        ) {
          return [
            {
              id: "special-here",
              kind: "here",
              label: "@here",
              subtitle:
                ctx.contextType === "group"
                  ? "Notify everyone in this group"
                  : "Notify everyone in this channel",
              sortKey: "here",
            },
          ];
        }
        return [];
      }
    },
    render: () => {
      let component;
      let selectedIndex = 0;
      let currentItems = [];
      let currentCommand = null;
      let currentClientRect = null;

      const updateProps = () => {
        component?.updateProps({
          items: currentItems,
          selectedIndex,
          clientRect: currentClientRect?.() || null,
          kind: kind === "channel" ? "channel" : "mention",
          onHover: (index) => {
            selectedIndex = index;
            updateProps();
          },
          onSelect: (item) => {
            currentCommand?.(item);
          },
        });
      };

      return {
        onStart: (props) => {
          selectedIndex = 0;
          currentItems = props.items || [];
          currentCommand = props.command;
          currentClientRect = props.clientRect;
          component = new ReactRenderer(ReferenceSuggestionList, {
            props: {
              items: currentItems,
              selectedIndex,
              clientRect: currentClientRect?.() || null,
              kind: kind === "channel" ? "channel" : "mention",
              onHover: (index) => {
                selectedIndex = index;
                updateProps();
              },
              onSelect: (item) => props.command(item),
            },
            editor: props.editor,
          });
        },
        onUpdate: (props) => {
          selectedIndex = 0;
          currentItems = props.items || [];
          currentCommand = props.command;
          currentClientRect = props.clientRect;
          updateProps();
        },
        onKeyDown: (props) => {
          if (props.event.key === "Escape") {
            component?.destroy();
            component = null;
            return true;
          }
          if (props.event.key === "ArrowUp") {
            selectedIndex =
              (selectedIndex + currentItems.length - 1) %
              Math.max(currentItems.length, 1);
            updateProps();
            return true;
          }
          if (props.event.key === "ArrowDown") {
            selectedIndex =
              (selectedIndex + 1) % Math.max(currentItems.length, 1);
            updateProps();
            return true;
          }
          if (props.event.key === "Enter" || props.event.key === "Tab") {
            props.event.preventDefault();
            const item = currentItems[selectedIndex];
            if (item) {
              currentCommand?.(item);
              return true;
            }
            // Keep Tab/Enter from falling through to send/newline while the
            // menu is open but still loading / empty.
            return true;
          }
          return false;
        },
        onExit: () => {
          component?.destroy();
          component = null;
        },
      };
    },
    command: ({ editor, range, props: item }) => {
      const ctx = getContext?.() || {};
      if (!item) return;
      if (ctx.socialMode) {
        if (item.kind === "social-user" && item.username) {
          const href = socialUserReferenceHref(item.username);
          const label = `@${item.displayName || item.username}`;
          insertReferenceLink(editor, range, { href, label });
          ctx.onReferenceInserted?.(item);
        }
        return;
      }
      if (!ctx.orgId) return;

      // Channel broadcast — plain text so API @here detection matches.
      if (item.kind === "here") {
        const chain = editor.chain().focus();
        if (range && range.from !== range.to) {
          chain.deleteRange(range);
        }
        chain.insertContent("@here ").unsetMark("link").run();
        ctx.onReferenceInserted?.(item);
        return;
      }

      let href;
      let label;
      if (item.kind === "channel") {
        href = channelReferenceHref(ctx.orgId, item.targetId);
        label = `#${String(item.title || "channel").replace(/^#/, "")}`;
      } else if (item.kind === "team") {
        href = teamReferenceHref(ctx.orgId, item.targetId);
        label = `@${item.displayName || "Team"}`;
      } else {
        href = userReferenceHref(ctx.orgId, item.targetId);
        label = `@${item.displayName || "User"}`;
      }

      if (item.canInvite && typeof ctx.onInviteAndMention === "function") {
        // Remove the @query immediately so it cannot leak into drafts, then
        // insert the mention at the current cursor only after invite succeeds.
        editor.chain().focus().deleteRange(range).run();
        ctx.onInviteAndMention(item, {
          editor,
          insertMention: () => {
            insertReferenceLink(
              editor,
              // Empty range at current selection — insertReferenceLink deletes
              // then inserts; use a no-op delete via equal from/to.
              {
                from: editor.state.selection.from,
                to: editor.state.selection.from,
              },
              { href, label }
            );
            ctx.onReferenceInserted?.(item);
          },
        });
        return;
      }

      insertReferenceLink(editor, range, { href, label });
      ctx.onReferenceInserted?.(item);
    },
  };
}

/**
 * TipTap extension enabling @user and #channel suggestions when context is provided.
 */
export function createReferenceSuggestionsExtension(getContext) {
  return Extension.create({
    name: "circaReferenceSuggestions",
    addOptions() {
      return {
        getContext,
      };
    },
    addProseMirrorPlugins() {
      const getter = () => this.options.getContext?.() || {};
      return [
        Suggestion({
          editor: this.editor,
          ...buildSuggestion({
            char: "@",
            kind: "user",
            getContext: getter,
            pluginKey: userSuggestionPluginKey,
          }),
        }),
        Suggestion({
          editor: this.editor,
          ...buildSuggestion({
            char: "#",
            kind: "channel",
            getContext: getter,
            pluginKey: channelSuggestionPluginKey,
          }),
        }),
      ];
    },
  });
}
